import React from 'react';
import { func, bool, shape, string } from 'prop-types';

import classnames from 'classnames';
import Card from '@andes/card';

import Link from '../../../../../components-v2/link';
import ItemHighlight from '../../../../card/highlight/highlight-label';
import { useStaticProps } from '../../../../context/static-props';
import { namespace } from '../../../../../components-v2/layout/constants';
import { verticalContentRenderer } from '../../../../map/vertical-content';
import { ImageComponent } from '../infowindow-image-component';

/**
 * @typedef {Object} FloatHighlight
 * @property {string} background - Background color of the highlight
 * @property {string} className - Additional CSS class for the highlight
 * @property {string} id - Unique identifier for the highlight
 * @property {Object} label - Label configuration
 * @property {string} label.text - Text displayed in the label
 * @property {string} label.color - Color of the label text
 * @property {string} type - Type of highlight
 */

/**
 * @typedef {Object} ItemProps
 * @property {string} title - Title of the item
 * @property {string} permalink - URL link for the item
 * @property {string} vertical - Vertical type of the item
 * @property {Object} pictures - Pictures associated with the item
 * @property {FloatHighlight} [float_highlight] - Highlight information for the item
 */

/**
 *
 * Renders a card with item information inside a Google Maps infowindow.
 * Displays item image, highlight label (if available), and content based on the vertical type.
 *
 * @param {Object} props - Component props
 * @param {ItemProps} props.item - Item data to be displayed
 * @param {Function} [props.onClick] - Handler for click events on the item
 * @param {Object} [props.options] - Additional display options
 * @param {boolean} [props.options.lowEnd] - Flag for low-end device optimization
 * @returns {React.ReactElement} Rendered InfowindowItem component
 */

const InfowindowItem = ({ item, onClick }) => {
  const { deviceType } = useStaticProps();
  const { title, permalink, float_highlight: floatHighlight } = item;

  return (
    <Card animated={false} className={classnames(namespace, `${namespace}__infowindow`)}>
      <div className={`${namespace}__wrapper`}>
        {floatHighlight && <ItemHighlight {...floatHighlight} className={`${namespace}__infowindow-seen-label`} />}
        <ImageComponent item={item} onClick={onClick} />
        <Link
          className={`${namespace}__content-link`}
          href={permalink}
          title={title}
          isInternal={false}
          target="_blank"
          onClick={onClick}
        >
          {verticalContentRenderer(item, deviceType)}
        </Link>
      </div>
    </Card>
  );
};

InfowindowItem.propTypes = {
  float_highlight: shape({
    background: string,
    className: string,
    id: string,
    label: shape({
      text: string,
      color: string,
    }),
    type: string,
  }),
  item: shape({
    title: string.isRequired,
    permalink: string.isRequired,
    vertical: string.isRequired,
    pictures: shape({}),
  }).isRequired,
  options: shape({
    lowEnd: bool,
  }),
  onClick: func,
};

InfowindowItem.defaultProps = {
  options: undefined,
};

export default React.memo(InfowindowItem);
